'use client'

import { useTranslations } from 'next-intl'
import PageSizeSelector from '../page-size-selector'
import ProductItem from './product-item'
import ProductsSortSelector from './products-sort-selector'
import { TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { ProductType, ProductsSortByEnum } from '@/types/product'
import { SortEnum } from '@/enums'

type PropsType = {
  items: Array<ProductType & { detailHref: string }>
  total: number
  size: number
  isAuthenticated: boolean
  // Optional controlled sort/size handlers for the client-side products browser. When omitted, the
  // selectors fall back to their default URL-driven navigation.
  sortBy?: ProductsSortByEnum
  sort?: SortEnum
  onSortChange?: (sortBy: ProductsSortByEnum, sort: SortEnum) => void
  onSizeChange?: (size: number) => void
}

const ProductsList = ({ items, total, size, isAuthenticated, sortBy, sort, onSortChange, onSizeChange }: PropsType) => {
  const t = useTranslations()

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div className="text-muted-foreground text-sm">{t('x_products_found', { count: total })}</div>
        <div className="flex items-center gap-3">
          <PageSizeSelector currentSize={size} onChange={onSizeChange} />
          <ProductsSortSelector sortBy={sortBy} sort={sort} onChange={onSortChange} />
        </div>
      </div>

      <ScrollArea className="rounded-lg border">
        <table className="w-full caption-bottom text-sm">
          <TableHeader>
            <TableRow className="bg-muted text-xs font-semibold tracking-wide uppercase">
              <TableHead />
              <TableHead>{t('product_description')}</TableHead>
              <TableHead className="w-32">{t('product_code')}</TableHead>
              <TableHead className="w-48">{t('oem_no')}</TableHead>
              <TableHead className="w-40">{t('brands')}</TableHead>
              <TableHead className="w-40">{t('models')}</TableHead>
              <TableHead className="w-28">{t('stock_status')}</TableHead>
              <TableHead className="w-36 text-right">{t('price')}</TableHead>
              <TableHead className="w-14" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(product => (
              <ProductItem
                key={product.id}
                product={product}
                detailHref={product.detailHref}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </TableBody>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  )
}

export default ProductsList
